import { SearchOutlined, FileExcelOutlined, EditOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Col, ConfigProvider, Input, Row, Space, Spin, Table, Tooltip } from "antd";
import frFR from "antd/locale/fr_FR";
import * as XLSX from "xlsx";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { searchAssure } from "../../hooks/useSearch";
import { highlightMatch } from "../../variables/stringFunctions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";

const SS_KEY = "filters:assures:v2";

/**
 * Assurés V2 : KPI total, recherche serveur unique (débouncée) + filtres persistants
 * (sessionStorage), colonne Actions (voir/éditer le profil), export Excel de la liste filtrée.
 * Conserve l'autocomplete de la navbar de la v1.
 */
const AssuresV2 = () => {
  const navigate = useNavigate();

  const SAVED = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "null") || {}; }
    catch (e) { return {}; }
  }, []);

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(SAVED.currentPage || 1);
  const [pageSize, setPageSize] = useState(SAVED.pageSize || 10);
  const [search, setSearch] = useState(SAVED.search || "");
  const [exporting, setExporting] = useState(false);

  // Autocomplete navbar (repris v1).
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  // Persistance des filtres.
  useEffect(() => {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ search, currentPage, pageSize }));
  }, [search, currentPage, pageSize]);

  const mapClient = (client, key) => ({
    id: key + 1,
    nom: client.nom,
    prenom: client.prenom,
    contact: client.contact?.[0]
      ? (client.contact?.[1] ? `${client.contact[0]} - ${client.contact[1]}` : `${client.contact[0]}`)
      : "",
    email: client.email,
    numeroAssure: client.numeroAssureP,
    adresse: client.adresse,
    data: client,
    codeId: client.codeId,
  });

  const fetchClients = async (page, limit, searchTerm) => {
    setLoadingClients(true);
    try {
      const res = await axiosInstance.get(`/api/personne/clients`, {
        params: { page: page - 1, size: limit, search: searchTerm || "" },
      });
      setClients((res.data.content || []).map(mapClient));
      setTotal(res.data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Chargement + rechargement débouncé sur la recherche serveur.
  useEffect(() => {
    const handler = setTimeout(() => fetchClients(currentPage, pageSize, search), 350);
    return () => clearTimeout(handler);
  }, [currentPage, pageSize, search]);

  const handleChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const onSearchChange = (v) => { setSearch(v); setCurrentPage(1); };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axiosInstance.get(`/api/personne/clients`, {
        params: { page: 0, size: 100000, search: search || "" },
      });
      const rows = (res.data.content || []).map((c) => ({
        "Nom": c.nom || "", "Prénom(s)": c.prenom || "", "N° Assuré": c.numeroAssureP || "",
        "Contact": c.contact?.[0] ? (c.contact?.[1] ? `${c.contact[0]} - ${c.contact[1]}` : `${c.contact[0]}`) : "",
        "Email": c.email || "", "Adresse": c.adresse || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Assurés");
      XLSX.writeFile(wb, `assures-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { console.log(e); }
    finally { setExporting(false); }
  };

  const columns = [
    {
      title: "Nom & Prénom(s)", dataIndex: "nom", key: "1",
      render: (_, record) => <span style={{ fontWeight: 600 }}>{record.nom} {record.prenom}</span>,
    },
    { title: "Numéro Assuré", dataIndex: "numeroAssure", key: "2" },
    { title: "Contact", dataIndex: "contact", key: "3" },
    { title: "Email", dataIndex: "email", key: "4" },
    { title: "Adresse", dataIndex: "adresse", key: "5" },
    {
      title: "Actions", key: "actions", align: "center",
      render: (_, record) => (
        <Tooltip title="Voir / éditer le profil">
          <Button type="text" icon={<EditOutlined />} style={{ color: COLORS.secondary }}
            onClick={() => navigate(`/modifier-profil/${record.codeId}`)} />
        </Tooltip>
      ),
    },
  ];

  const renderSearchResults = (results) => (
    <div className="px-3 py-2">
      {results.map((item, index) => (
        <div key={index} onClick={() => navigate(`/modifier-profil/${item.codeId}`)}
          className="dropdown-item d-flex align-items-center gap-2 py-1" style={{ cursor: "pointer" }}>
          <span className="text-truncate">
            {[item?.nom, item?.prenom].map((text, i) => (
              <React.Fragment key={i}>{i > 0 && " "}{highlightMatch(text?.toString(), searchValue)}</React.Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchAssure(searchValue).then((res) => setSearchResults(res.data.content)).catch((err) => console.log(err));
    }
  }, [searchValue]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"ASSURES"} />
        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Row style={{ marginBottom: 15 }} gutter={[12, 12]}>
                <Col flex="1" style={{ minWidth: 200 }}>
                  <StatCard title="Assurés" subtitle="Nombre total d'assurés" value={displayNumbers(total || 0)}
                    icon="bx bx-user" iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                </Col>
              </Row>

              <div className="card">
                <div className="card-body">
                  <Row gutter={[12, 12]} align="middle" justify="space-between">
                    <Col xs={24} md={12}>
                      <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                        placeholder="Rechercher un assuré (nom, n° assuré, contact)…"
                        value={search} onChange={(e) => onSearchChange(e.target.value)} />
                    </Col>
                    <Col xs={24} md={6} style={{ textAlign: "right" }}>
                      <Button icon={<FileExcelOutlined />} loading={exporting} onClick={exportExcel}
                        style={{ borderColor: "#059669", color: "#059669" }}>Export Excel</Button>
                    </Col>
                  </Row>
                </div>
                <ConfigProvider locale={frFR}>
                  <Spin spinning={loadingClients}>
                    <Table
                      columns={columns}
                      rowKey="codeId"
                      dataSource={clients}
                      onChange={handleChange}
                      scroll={{ x: "max-content" }}
                      pagination={{
                        current: currentPage, pageSize, total, showSizeChanger: true,
                        showTotal: (t) => `${t} assuré(s)`,
                      }}
                    />
                  </Spin>
                </ConfigProvider>
              </div>
            </div>
            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>
      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default AssuresV2;
